// App.jsx (public session view)
import React, { useEffect, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  useLazyGetSavedToursSessionQuery,
  useApplyBulkDiscountMutation,
} from "../../../app/services/book/saved_tours"
import TourCard from "../save_tours/components/TourCard"
import { groupTours } from "../save_tours/utils/parsers/tourse"
import DiscountModal from "../save_tours/components/DiscountModal"
import {
  Tag,
  CheckSquare,
  X,
  Copy,
  Check,
  Layers,
  Globe,
  ChevronDown,
  LayoutTemplate,
  Share2,
  Send,
} from "lucide-react"
import { useRequestConsultationMutation } from "../../../app/services/book/shared_tours"
import ConsultationModal from "../save_tours/components/ConsultationModal"
import TourShareTemplatesModal from "../save_tours/components/TourShareTemplatesModal/TourShareTemplatesModal"
import ShareModal from "../save_tours/components/ShareModal/ShareModal"

const buildSessionUrl = (sessionKey, locale) =>
  `${window.location.origin}/shared_tours/${sessionKey}`

const formatSessionNumber = (n) => (n != null ? `SES-${String(n).padStart(6, "0")}` : "—")

const SUPPORTED_LOCALES = [
  { code: "ro", label: "RO" },
  { code: "ru", label: "RU" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "bg", label: "BG" },
  { code: "uk", label: "UA" },
]

const LanguageSelector = () => {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const current = SUPPORTED_LOCALES.find((l) => l.code === i18n.language) || SUPPORTED_LOCALES[0]

  const handleSelect = (code) => {
    i18n.changeLanguage(code)
    setOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
      >
        <Globe size={14} />
        {current.label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1.5 w-24 bg-white border border-[#E7EAEF] rounded-lg shadow-lg z-40 py-1">
            {SUPPORTED_LOCALES.map((l) => (
              <button
                key={l.code}
                onClick={() => handleSelect(l.code)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#F4F6F9] ${
                  l.code === current.code ? "text-[#26303D] font-medium" : "text-[#5A6779]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const App = ({
  sessionKey,
  isAdmin = false,
  editable = false,
  requestConsultation = false,
  languageSwitcher = false,
  telegramBotUrl = null,
}) => {
  const { t, i18n } = useTranslation()

  const [showPhotos, setShowPhotos] = useState(false)
  const [selectMode, setSelectMode] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState(new Set())
  const [discountModalOpen, setDiscountModalOpen] = useState(false)
  const [consultationTarget, setConsultationTarget] = useState(null) // tour object or null
  const [copied, setCopied] = useState(false)
  const [templatesManagerOpen, setTemplatesManagerOpen] = useState(false)
  const [sessionShareOpen, setSessionShareOpen] = useState(false)

  const [fetchSession, { data, isLoading, isFetching, isError }] =
    useLazyGetSavedToursSessionQuery()
  const [applyBulkDiscount] = useApplyBulkDiscountMutation()
  const [submitConsultation] = useRequestConsultationMutation()

  useEffect(() => {
    if (sessionKey) fetchSession(sessionKey)
  }, [fetchSession, sessionKey])

  const session = data?.session
  const grouped = useMemo(() => (session ? groupTours(session.tours) : []), [session])

  const selectedIds = useMemo(() => {
    return grouped
      .filter((g) => selectedKeys.has(String(g.id)))
      .flatMap((g) => (g.offers || []).map((o) => o.id))
      .filter(Boolean)
  }, [grouped, selectedKeys])

  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelectedKeys(new Set())
  }

  const handleApplyDiscount = async ({ discount_type, discount_value }) => {
    await applyBulkDiscount({
      session_key: sessionKey,
      ids: selectedIds,
      discount_type,
      discount_value,
    })
    setDiscountModalOpen(false)
    exitSelectMode()
    fetchSession(sessionKey)
  }

  const handleSubmitConsultation = async (leadForm) => {
    const res = await submitConsultation({
      sessionKey,
      tourId: consultationTarget?.id,
      lead: leadForm,
    })
    if (res.error) {
      const message =
        res.error?.data?.errors?.join(", ") ||
        res.error?.data?.message ||
        t("saved_tours.consultation_submit_error")
      throw new Error(message)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildSessionUrl(sessionKey, i18n.language))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  if (isLoading) {
    return <div className="text-center py-20 text-[#9AA5B4]">{t("saved_tours.loading_tours")}</div>
  }

  if (isError || !session) {
    return (
      <div className="text-center py-20 text-[#9AA5B4]">{t("saved_tours.session_not_found")}</div>
    )
  }

  return (
    <div className="min-h-screen w-full bg-[#F4F6F9] py-6 md:py-10 px-4 md:px-8 pb-10">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-lg font-semibold text-[#1F2733]" title={session.session_key}>
                {t("saved_tours.session", {
                  sessionKey: formatSessionNumber(session.session_number),
                })}
              </h1>
              <div className="text-sm text-[#9AA5B4]">
                <Layers size={12} />{" "}
                {t("saved_tours.tours_count", {
                  count: session.tours_count,
                })}
                {session.temporary && ` · ${t("saved_tours.draft")}`}
              </div>
            </div>

            {editable && (
              <button
                onClick={handleCopyLink}
                title={t("saved_tours.copy_link")}
                className="p-2 rounded-lg text-[#9AA5B4] hover:text-[#26303D] hover:bg-[#EEF1F5] transition-colors"
              >
                {copied ? <Check size={17} className="text-[#1E9E5B]" /> : <Copy size={17} />}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {languageSwitcher && <LanguageSelector />}

            {requestConsultation && telegramBotUrl && (
              <a
                href={telegramBotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-white bg-[#229ED9] rounded-lg px-3 py-1.5 hover:bg-[#1c8cc2]"
              >
                <Send size={14} /> {t("saved_tours.write_in_telegram", "Написать в Telegram")}
              </a>
            )}

            {editable && (
              <button
                onClick={() => setSessionShareOpen(true)}
                className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
              >
                <Share2 size={14} /> {t("saved_tours.share.title_session")}
              </button>
            )}

            {editable && (
              <button
                onClick={() => setTemplatesManagerOpen(true)}
                className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
              >
                <LayoutTemplate size={14} /> {t("saved_tours.share.templates")}
              </button>
            )}

            {editable &&
              (!selectMode ? (
                <button
                  onClick={() => setSelectMode(true)}
                  className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
                >
                  <CheckSquare size={14} /> {t("saved_tours.select_tours_for_discount")}
                </button>
              ) : (
                <button
                  onClick={exitSelectMode}
                  className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
                >
                  <X size={14} /> {t("saved_tours.cancel_selection")}
                </button>
              ))}
          </div>
        </div>

        {isFetching && (
          <div className="text-sm text-[#9AA5B4] px-1 mb-2">{t("saved_tours.updating_list")}</div>
        )}

        <div className="flex flex-col gap-4">
          {grouped.map((tour, i) => {
            const key = String(tour.id)
            return (
              <TourCard
                key={key}
                tour={tour}
                index={i}
                sessionKey={sessionKey}
                showPhotos={showPhotos}
                editable={editable}
                onChanged={() => fetchSession(sessionKey)}
                selectable={selectMode}
                selected={selectedKeys.has(key)}
                onToggleSelect={() => toggleKey(key)}
                requestConsultation={requestConsultation}
                onRequestConsultation={() => setConsultationTarget(tour)}
              />
            )
          })}
        </div>

        {grouped.length === 0 && (
          <div className="text-center py-20 text-[#9AA5B4] bg-white rounded-2xl border border-[#E7EAEF]">
            {t("saved_tours.no_tours_in_session")}
          </div>
        )}
      </div>

      {selectMode && selectedKeys.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E7EAEF] shadow-lg px-4 py-3 flex items-center justify-between z-40">
          <div className="text-sm text-[#5A6779]">
            {t("saved_tours.selected_tours")}:{" "}
            <span className="font-semibold text-[#1F2733]">{selectedKeys.size}</span>
          </div>
          <button
            onClick={() => setDiscountModalOpen(true)}
            className="flex items-center gap-1.5 text-sm text-white bg-[#26303D] rounded-lg px-4 py-2"
          >
            <Tag size={14} /> {t("saved_tours.apply_discount")}
          </button>
        </div>
      )}

      <DiscountModal
        open={discountModalOpen}
        mode="bulk"
        onClose={() => setDiscountModalOpen(false)}
        onSubmit={handleApplyDiscount}
      />

      <ConsultationModal
        open={!!consultationTarget}
        onClose={() => setConsultationTarget(null)}
        onSubmit={handleSubmitConsultation}
        tour={consultationTarget}
        toursCount={1}
      />

      <TourShareTemplatesModal
        open={templatesManagerOpen}
        onClose={() => setTemplatesManagerOpen(false)}
      />

      <ShareModal
        target={{ type: "session", sessionKey }}
        link={buildSessionUrl(sessionKey, i18n.language)}
        open={sessionShareOpen}
        onClose={() => setSessionShareOpen(false)}
      />
    </div>
  )
}

export default App
